import { useState, useEffect, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { fetchFinancialSnapshot } from "../graphClient";
import {
  formatBigDollars,
  formatExactDollars,
  formatDate,
} from "../lib/format";

const ENTITY_LABELS = {
  LeanneDew: "Leanne",
  CarlDew: "Carl",
  SandeuTrust: "Sandeu",
  RSF: "RSF",
  CILK: "CILK",
};

// Donut colours — navy/steel/ice base plus warm + slate accents so all five
// entity segments stay distinguishable on a white background.
const ENTITY_COLOURS = {
  LeanneDew: "#1B3A5C",
  RSF: "#4A6D8C",
  SandeuTrust: "#5DA9C7",
  CarlDew: "#E89B5C",
  CILK: "#94A3B8",
};

function rollUp(items) {
  const map = new Map();
  for (const item of items) {
    if (!map.has(item.entity)) {
      map.set(item.entity, {
        name: item.entity,
        label: ENTITY_LABELS[item.entity] || item.entity,
        assets: 0,
        liabilities: 0,
        net: 0,
      });
    }
    const ent = map.get(item.entity);
    ent.net += item.amount;
    if (item.amount >= 0) ent.assets += item.amount;
    else ent.liabilities += item.amount;
  }
  return Array.from(map.values()).sort((a, b) => b.net - a.net);
}

function DonutChart({ slices, total, size = 220, thickness = 26 }) {
  const radius = (size - thickness) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * radius;

  let offset = 0;
  const elements = slices.map((slice, i) => {
    const fraction = total > 0 ? slice.value / total : 0;
    const dash = circumference * fraction;
    const el = (
      <circle
        key={i}
        cx={cx}
        cy={cy}
        r={radius}
        fill="none"
        stroke={slice.color}
        strokeWidth={thickness}
        strokeDasharray={`${dash} ${circumference}`}
        strokeDashoffset={-offset}
        strokeLinecap="butt"
      />
    );
    offset += dash;
    return el;
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <g transform={`rotate(-90 ${cx} ${cy})`}>
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="#F1F5F9"
          strokeWidth={thickness}
        />
        {elements}
      </g>
    </svg>
  );
}

function StatusPill({ children }) {
  return (
    <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-full px-3 py-1 text-xs font-medium text-slate-600">
      {children}
    </span>
  );
}

export default function FinancialSnapshotView() {
  const { instance, accounts } = useMsal();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchFinancialSnapshot(instance, accounts[0]);
      setItems(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const entities = useMemo(() => rollUp(items), [items]);

  const totals = useMemo(() => {
    let assets = 0;
    let liabilities = 0;
    for (const item of items) {
      if (item.amount >= 0) assets += item.amount;
      else liabilities += item.amount;
    }
    return { assets, liabilities, net: assets + liabilities };
  }, [items]);

  const lastUpdated = useMemo(() => {
    let max = null;
    for (const item of items) {
      if (item.lastUpdated && (!max || item.lastUpdated > max)) {
        max = item.lastUpdated;
      }
    }
    return max;
  }, [items]);

  const donutSlices = entities.map((ent) => ({
    label: ent.label,
    value: Math.max(ent.net, 0),
    color: ENTITY_COLOURS[ent.name] || "#94A3B8",
  }));
  const donutTotal = donutSlices.reduce((a, b) => a + b.value, 0);

  return (
    <div>
      <h2 className="text-lg font-semibold text-slate-900 tracking-tight mb-4 px-1">
        Financial Snapshot
      </h2>

      {loading && (
        <div className="bg-white rounded-2xl border border-slate-200/70 p-12 text-center text-slate-400">
          Loading snapshot…
        </div>
      )}

      {error && (
        <div className="bg-white rounded-2xl border border-slate-200/70 p-6 text-center">
          <p className="text-slate-900 text-sm font-medium mb-2">
            Couldn&rsquo;t load the snapshot
          </p>
          <p className="text-slate-500 text-xs mb-4 break-all">{error}</p>
          <button
            onClick={load}
            className="text-sm bg-[#1B3A5C] hover:bg-[#0E243B] text-white px-4 py-2 rounded-xl font-medium transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="space-y-4">
          {/* Hero card */}
          <section className="bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_3px_rgba(15,23,42,0.05),0_1px_2px_rgba(15,23,42,0.06)] p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center gap-6">
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Net worth
                </p>
                <p className="text-5xl sm:text-6xl font-semibold text-[#1B3A5C] tracking-tight mt-2 tabular-nums">
                  {formatBigDollars(totals.net)}
                </p>
                <p className="text-sm text-slate-500 mt-1 tabular-nums">
                  {formatExactDollars(totals.net)}
                </p>
                {lastUpdated && (
                  <div className="flex items-center gap-2 mt-4 flex-wrap">
                    <StatusPill>All entities</StatusPill>
                    <StatusPill>Updated {formatDate(lastUpdated)}</StatusPill>
                  </div>
                )}
              </div>

              <div className="relative shrink-0 self-center">
                <DonutChart slices={donutSlices} total={donutTotal} />
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                    Allocation
                  </p>
                  <p className="text-base font-semibold text-[#1B3A5C] mt-0.5">
                    {entities.length} entities
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Stats row */}
          <section className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                Assets
              </p>
              <p className="text-2xl sm:text-3xl font-semibold text-[#1B3A5C] tracking-tight mt-2 tabular-nums">
                {formatBigDollars(totals.assets)}
              </p>
              <p className="text-xs text-slate-500 mt-1 tabular-nums">
                {formatExactDollars(totals.assets)}
              </p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                Liabilities
              </p>
              <p className="text-2xl sm:text-3xl font-semibold text-[#B45309] tracking-tight mt-2 tabular-nums">
                {formatBigDollars(Math.abs(totals.liabilities))}
              </p>
              <p className="text-xs text-slate-500 mt-1 tabular-nums">
                {formatExactDollars(Math.abs(totals.liabilities))}
              </p>
            </div>
          </section>

          {/* By entity */}
          <section>
            <div className="flex items-baseline justify-between mb-3 px-1">
              <h3 className="text-sm font-semibold text-slate-700 tracking-tight">
                By entity
              </h3>
              <span className="text-xs text-slate-400">
                {entities.length} entities
              </span>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden divide-y divide-slate-100">
              {entities.map((ent) => {
                const pct =
                  donutTotal > 0 ? Math.max(ent.net, 0) / donutTotal : 0;
                const color = ENTITY_COLOURS[ent.name] || "#94A3B8";
                return (
                  <div key={ent.name} className="px-5 py-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: color }}
                        />
                        <span className="font-medium text-[#1B3A5C] text-base truncate">
                          {ent.label}
                        </span>
                      </div>
                      <span className="font-semibold text-[#1B3A5C] text-base tabular-nums">
                        {formatBigDollars(ent.net)}
                      </span>
                    </div>
                    <div className="mt-3 flex items-center gap-3">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${(pct * 100).toFixed(1)}%`,
                            backgroundColor: color,
                          }}
                        />
                      </div>
                      <span className="text-xs font-medium text-slate-500 tabular-nums w-10 text-right">
                        {(pct * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-4 text-xs text-slate-500 flex-wrap">
                      <span>
                        Assets{" "}
                        <span className="text-slate-700 font-medium tabular-nums">
                          {formatExactDollars(ent.assets)}
                        </span>
                      </span>
                      {ent.liabilities < 0 && (
                        <span>
                          Liabilities{" "}
                          <span className="text-amber-700 font-medium tabular-nums">
                            {formatExactDollars(ent.liabilities)}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
